import { HideImage } from '@mui/icons-material'
import { Button, Dialog, DialogContent, Grid } from '@mui/material'
import { ExplanationAccordion } from 'components'
import * as controllers from 'controllers'
import { VehicleCheckInImporterDoorLabelPhotoDto, vehicleCheckInImporterDoorLabelPhotoValidationSchema } from 'dtos'
import { Formik } from 'formik'
import {
  CheckInPhotoBrowseTakeRow,
  CheckInPhotoDialogNavProps,
  CheckInPhotoDialogTitleRow,
  CheckInPhotoPreview,
  CheckInPhotoStackedNavButtons,
  useCheckInPhotoSubmitIntent,
  useLifecycleDocumentPreview
} from './checkInPhotoDialogCommon'

interface ImporterDoorLabelPhotoDialogProps {
  initialValues: VehicleCheckInImporterDoorLabelPhotoDto
  isLoading: boolean
  nav?: CheckInPhotoDialogNavProps
  onClose: () => void
  onDelete: () => void
  onSave: (values: VehicleCheckInImporterDoorLabelPhotoDto, options?: { goToNext?: boolean }) => void
  open: boolean
  vehicleGuid: string
}

export default function ImporterDoorLabelPhotoDialog({
  initialValues,
  isLoading,
  nav,
  onClose,
  onDelete,
  onSave,
  open,
  vehicleGuid
}: ImporterDoorLabelPhotoDialogProps) {
  const { onFormSubmit, submitClose, submitThenNext } = useCheckInPhotoSubmitIntent(onSave)
  const serverSrc = useLifecycleDocumentPreview(
    vehicleGuid,
    initialValues.documentGuid,
    initialValues.displayFileName,
    controllers.getVehicleCheckInPhotoBase64
  )

  return (
    <Dialog open={open}>
      <Formik
        enableReinitialize={true}
        initialValues={initialValues}
        validateOnChange={true}
        validateOnBlur={false}
        validationSchema={vehicleCheckInImporterDoorLabelPhotoValidationSchema}
        onSubmit={onFormSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm, values }) => {
          return (
            <form onSubmit={e => void e.preventDefault()}>
              <DialogContent>
                <Grid container spacing={2} alignItems='center'>
                  <CheckInPhotoDialogTitleRow onClose={onClose} title='Importer Door Label Photo Details' />

                  <Grid item xs={12}>
                    <CheckInPhotoPreview
                      alt='Importer Door Label'
                      file={values.vehicleCheckInImporterDoorLabelPhotoFile}
                      placeholderSrc='/3 Importer Door Label.jpg'
                      serverSrc={serverSrc}
                    />
                  </Grid>

                  <CheckInPhotoBrowseTakeRow
                    browseInputId='importer-door-browse'
                    cameraInputId='importer-door-camera'
                    disabled={isSubmitting || isLoading}
                    onFileChosen={file => setFieldValue('vehicleCheckInImporterDoorLabelPhotoFile', file)}
                  />

                  <Grid item xs={12}>
                    <Button
                      color='error'
                      disabled={isSubmitting || isLoading || !Boolean(values.documentGuid)}
                      endIcon={<HideImage />}
                      fullWidth
                      onClick={() => {
                        if (window.confirm('Are you sure you would like to delete the uploaded Importer Door Label?')) {
                          onDelete()
                        }
                      }}
                      variant='outlined'
                    >
                      DELETE UPLOADED PHOTO
                    </Button>
                  </Grid>

                  <Grid item xs={12}>
                    <ExplanationAccordion variant='outlined'>
                      || The “Browse or Take Photo” button is disabled if Convert Cluster = Yes was selected on the “Vehicle
                      Details” step of vehicle check in. || “Delete Uploaded Photo” can only be pressed if a user has uploaded
                      an image. || “Submit” can only be pressed if a user has uploaded an image. Pressing “Submit” will
                      associate the user-uploaded image with the vehicle record and return the user to the Check-In Photos
                      gallery screen. || Pressing “Previous” goes to the previous photo without saving. ||
                    </ExplanationAccordion>
                  </Grid>

                  <CheckInPhotoStackedNavButtons
                    isLoading={isLoading}
                    isSubmitting={isSubmitting}
                    nav={nav}
                    submitClose={submitClose}
                    submitForm={submitForm}
                    submitThenNext={submitThenNext}
                  />
                </Grid>
              </DialogContent>
            </form>
          )
        }}
      </Formik>
    </Dialog>
  )
}
